import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AppProvider } from '../context/AppContext';
import MapTab from './tabs/MapTab';
// import SLDTab from './tabs/SLDTab'; // DELETED - Single Line Diagram removed
import NetworkGraphTab from './tabs/NetworkGraphTab';
import MetadataTab from './tabs/MetadataTab';
import EventHistoryTab from './tabs/EventHistoryTab';
import DocumentsTab from './tabs/DocumentsTab';
import WebBrowserTab from './tabs/WebBrowserTab';
import JupyterNotebookTab from './tabs/JupyterNotebookTab';
import { AppState } from '../types';
import { X } from 'lucide-react';

interface PopoutTabProps {
  tabType: string;
  title: string;
  windowId: string;
}

const tabComponents = {
  map: MapTab,
  // sld: SLDTab, // DELETED
  network: NetworkGraphTab,
  metadata: MetadataTab,
  events: EventHistoryTab,
  documents: DocumentsTab,
  browser: WebBrowserTab,
  notebook: JupyterNotebookTab,
};

const PopoutTab: React.FC<PopoutTabProps> = ({ tabType, title, windowId }) => {
  const [appState, setAppState] = useState<AppState>({
    selectedSite: 'site-1',
    selectedComponents: [],
    selectedConnections: [],
    activeTab: null,
    filterCriteria: {}
  });

  const TabComponent = tabComponents[tabType as keyof typeof tabComponents];

  useEffect(() => {
    // Set up communication with parent window
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'app-state-update') {
        setAppState(event.data.state);
      }
    };

    window.addEventListener('message', handleMessage);

    // Make updateAppState available globally for the window manager
    (window as any).updateAppState = (newState: AppState) => {
      setAppState(newState);
    };

    // Notify parent that this window is ready
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'popout-ready',
        windowId,
        tabType
      }, window.location.origin);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [windowId, tabType]);

  const handleClose = () => {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'close-popout',
        windowId
      }, window.location.origin);
    }
    window.close();
  };

  const sendStateUpdate = (updates: Partial<AppState>) => {
    const newState = { ...appState, ...updates };
    setAppState(newState);
    
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'state-update',
        windowId,
        state: newState
      }, window.location.origin);
    }
  };

  if (!TabComponent) {
    return (
      <div className="flex items-center justify-center h-full bg-engramiq-dark-blue">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-engramiq-light-grey mb-2">Unknown Tab Type</h2>
          <p className="text-engramiq-bright-grey">Tab type "{tabType}" is not recognized.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-engramiq-dark-blue font-figtree flex flex-col">
      {/* Header */}
      <div className="bg-gradient-green-blue p-3 flex items-center justify-between">
        <h1 className="text-lg font-bold text-engramiq-dark-blue">{title}</h1>
        <button
          onClick={handleClose}
          className="p-1 hover:bg-engramiq-dark-blue/20 rounded text-engramiq-dark-blue"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        <AppProvider initialState={appState} onStateChange={sendStateUpdate}>
          <TabComponent />
        </AppProvider>
      </div>
    </div>
  );
};

// Function to render PopoutTab in a new window
export const renderPopoutTab = (tabType: string, title: string, windowId: string, targetWindow: Window) => {
  // Wait for the window to be ready
  const checkReady = () => {
    const container = targetWindow.document.getElementById('root');
    if (container) {
      const root = createRoot(container);
      root.render(
        <PopoutTab 
          tabType={tabType} 
          title={title} 
          windowId={windowId} 
        />
      );
    } else {
      setTimeout(checkReady, 100);
    }
  };
  
  // Listen for the window-ready message from the pop-out window
  const messageHandler = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (event.data.type === 'popout-message' && 
        event.data.windowId === windowId && 
        event.data.type === 'window-ready') {
      window.removeEventListener('message', messageHandler);
      setTimeout(checkReady, 200); // Small delay to ensure DOM is ready
    }
  };
  
  window.addEventListener('message', messageHandler);
  
  // Fallback: also check when the window loads
  setTimeout(checkReady, 300);
};

export default PopoutTab;